import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { useCalls, useIncidents, usePortalClient, useRealtimeConnection } from '@ngsos/portal-core/portal';

import { PsapView } from '@/app/shell/PsapConfigurationProvider';
import { usePsapConfiguration } from '@/app/shell/psapConfiguration';
import {
  LoadingFailedBanner,
  LoadingFailedBox,
  NgButton,
  RealtimeConnectionStatusBanner,
} from '@/ui';
import { ATE_PSAP_ID } from '@/features/shared/knownPsapIds';
import { getQueryParamCaseInsensitive } from '@/features/shared/queryParams';
import { selectIncidentMapPoints } from './incidentListSelectors';
import { toRowFromIncident } from '@/features/shared/incidentListRow';
import type { IncidentListContext } from './incidentListContext';
import { IncidentListLayoutBox } from './IncidentListLayoutBox';
import { IncidentListContent } from './IncidentListContent';
import { IncidentListMapContent } from './IncidentListMapContent';
import { NewIncidentDialog } from './NewIncidentDialog';
import type { NewIncidentDialogHandle } from './NewIncidentDialog';
import { NewIncidentFromDataSentenceDialog } from './NewIncidentFromDataSentenceDialog';
import type { NewIncidentFromDataSentenceDialogHandle } from './NewIncidentFromDataSentenceDialog';

export function IncidentListPage() {
  const { t } = useTranslation();
  const psapConfiguration = usePsapConfiguration();
  const [searchParams] = useSearchParams();

  const defaultCountryCode = (psapConfiguration.psapPortalConfiguration.defaultCountryCode as string | null) ?? '';

  const [context, setContext] = useState<IncidentListContext>('incidents');

  const newIncidentDialogRef = useRef<NewIncidentDialogHandle>(null);
  const newIncidentFromDataSentenceDialogRef = useRef<NewIncidentFromDataSentenceDialogHandle>(null);

  const portalClient = usePortalClient();
  const connection = useRealtimeConnection();
  const { incidents, dataState } = useIncidents();
  const { calls: callNotifications } = useCalls();

  const isLoading = dataState === 'loading';
  const dataLoadFailed = dataState === 'failed';
  const hasLoadedData = incidents.length > 0 || callNotifications.length > 0;

  const retryDataLoad = async () => {
    await portalClient?.reload();
  };

  const requestedIncidentIdParam = getQueryParamCaseInsensitive(searchParams, 'requestedIncidentId');
  const phoneNumberParam = getQueryParamCaseInsensitive(searchParams, 'phoneNumber');
  const dialogOpenedFromQueryRef = useRef(false);

  useEffect(() => {
    if (isLoading || dialogOpenedFromQueryRef.current || requestedIncidentIdParam == null) {
      return;
    }

    dialogOpenedFromQueryRef.current = true;

    let phoneNumber = phoneNumberParam?.trim() ?? '';
    if (!phoneNumber.startsWith('+')) {
      phoneNumber = `${defaultCountryCode}${phoneNumber}`;
    }

    newIncidentDialogRef.current?.showModal({
      requestedIncidentId: requestedIncidentIdParam,
      phoneNumber,
    });
  }, [isLoading, requestedIncidentIdParam, phoneNumberParam, defaultCountryCode]);

  const createNewIncident = () => {
    newIncidentDialogRef.current?.showModal();
  };

  const showNewIncidentFromDataSentenceModal = () => {
    newIncidentFromDataSentenceDialogRef.current?.showModal();
  };

  const incidentMapPoints = selectIncidentMapPoints(incidents)?.map(toRowFromIncident);

  return (
    <>
      <IncidentListLayoutBox
        bannersArea={
          <>
            <RealtimeConnectionStatusBanner connection={connection} />

            {dataLoadFailed && hasLoadedData && <LoadingFailedBanner onRetry={retryDataLoad} />}
          </>
        }
        incidentListArea={
          dataLoadFailed && !hasLoadedData ? (
            <LoadingFailedBox onRetry={retryDataLoad} />
          ) : (
            <IncidentListContent
              isLoading={isLoading}
              onTabSwitch={setContext}
              incidents={incidents}
              callNotifications={callNotifications}
            />
          )
        }
        actionBarArea={
          <div className="d-flex flex-column flex-tablet-row gap-2 align-items-stretch">
            <NgButton text={t('Button_NewIncident')} icon="fal fa-plus-circle" onClick={createNewIncident} />

            <PsapView psapId={ATE_PSAP_ID}>
              <NgButton
                text={t('Button_NewIncidentFromDataSentence')}
                icon="fal fa-plus-circle"
                onClick={showNewIncidentFromDataSentenceModal}
              />
            </PsapView>
          </div>
        }
        mapArea={
          <IncidentListMapContent
            context={context}
            incidents={incidentMapPoints}
            callNotifications={callNotifications}
          />
        }
      />

      <NewIncidentDialog ref={newIncidentDialogRef} />
      <NewIncidentFromDataSentenceDialog ref={newIncidentFromDataSentenceDialogRef} />
    </>
  );
}
